/**
 * Admin Maintenance — Super Admin cockpit for the non-QC data-quality tools:
 *
 *   1. Missing ID Report            — read-only + CSV export.
 *   2. Identity Bulk Fill           — dry-run + token-gated apply for Iqama/NID gaps.
 *   3. Employee Duplicate Cleanup   — DRY-RUN + per-cluster "safe to merge" checkboxes
 *                                     + snapshot payload preview + snapshot batch action.
 *                                     Merge apply is intentionally NOT exposed here yet.
 *   4. Merge Undo Log               — list snapshot batches + one-click Restore.
 *   5. Restore Deleted Employee     — the undo button, wired up in advance.
 *
 * QC-specific tools live in the QC Violations hub (/qc-violations) — nothing
 * QC-related belongs on this page.
 *
 * All endpoints return `read_only: true` unless we explicitly confirm-string
 * a write. No cascading merges are triggered from this page yet.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Users,
  Undo2,
  Database,
  ShieldCheck,
  Eye,
  History,
  Save,
  FileDown,
  IdCard,
  Map,
  UploadCloud,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SAFE_MERGE_STORAGE_KEY = 'admin-maintenance-safe-to-merge-v1';

const truncate = (s, n = 12) => (s && s.length > n ? s.slice(0, n) + '…' : s);
const clusterId = (c) => `${c.signal}|${c.key}`;

// -----------------------------------------------------------------------------
// Section: Employee Duplicate Cleanup (DRY-RUN + per-cluster checkboxes)
// -----------------------------------------------------------------------------
const readSafeSet = () => {
  try {
    const raw = localStorage.getItem(SAFE_MERGE_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch { return new Set(); }
};
const writeSafeSet = (s) => {
  try { localStorage.setItem(SAFE_MERGE_STORAGE_KEY, JSON.stringify([...s])); }
  catch { /* localStorage might be disabled */ }
};

const EmployeeDuplicateCard = ({ token, onSnapshotCreated }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [safeSet, setSafeSet] = useState(readSafeSet);
  const [previewFor, setPreviewFor] = useState(null); // {key,payload,loading,error}
  const [snapReason, setSnapReason] = useState('');
  const [snapshotting, setSnapshotting] = useState(false);

  const fetchDryRun = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/employee-duplicate-diagnostic`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDryRun(); }, [fetchDryRun]);

  const toggleSafe = (id) => {
    setSafeSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      writeSafeSet(next);
      return next;
    });
  };
  const clearSafe = () => {
    const empty = new Set();
    writeSafeSet(empty);
    setSafeSet(empty);
  };

  // Only cluster keys that still exist in the current dry-run + are checked
  const visibleClusterIds = useMemo(
    () => new Set((data?.clusters || []).map(clusterId)),
    [data],
  );
  const selectedIds = useMemo(
    () => [...safeSet].filter((k) => visibleClusterIds.has(k)),
    [safeSet, visibleClusterIds],
  );

  const openPreview = async (id) => {
    setPreviewFor({ key: id, loading: true });
    try {
      const r = await axios.post(
        `${API}/admin/merge-undo/preview`,
        { cluster_keys: [id] },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviewFor({ key: id, payload: r.data });
    } catch (e) {
      setPreviewFor({ key: id, error: e.response?.data?.detail?.error || e.message });
    }
  };

  const snapshotSelected = async () => {
    if (!selectedIds.length) return;
    if (snapReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters.');
      return;
    }
    setSnapshotting(true);
    try {
      const r = await axios.post(
        `${API}/admin/merge-undo/snapshot?confirm=SNAPSHOT-BATCH`,
        { cluster_keys: selectedIds, reason: snapReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Snapshot batch created: ${r.data.total_snapshots} document(s) captured.`);
      setSnapReason('');
      clearSafe();
      onSnapshotCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setSnapshotting(false);
    }
  };

  const s = data?.stats;

  return (
    <Card data-testid="dedupe-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-5 w-5 text-amber-600" />
          Employee Duplicate Cleanup — Dry-run only
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="p-6 text-center text-slate-400" data-testid="dedupe-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : !data ? (
          <div className="text-sm text-slate-500">No data.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Stat label="Employees scanned" value={s.total_employees_scanned} />
              <Stat label="Clusters" value={s.total_clusters} highlight={s.total_clusters > 0} />
              <Stat label="Duplicate records" value={s.total_duplicate_records} highlight={s.total_duplicate_records > 0} />
              <Stat label="Would soft-delete" value={s.would_soft_delete} warn={s.would_soft_delete > 0} />
              <Stat label="Marked safe" value={selectedIds.length} good={selectedIds.length > 0} />
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-4">
              <Button variant="outline" onClick={fetchDryRun} disabled={loading} data-testid="dedupe-refresh">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
              </Button>
              <Button
                variant="outline"
                onClick={clearSafe}
                disabled={selectedIds.length === 0}
                data-testid="dedupe-clear-safe"
              >
                Clear selection
              </Button>
              <div className="text-xs text-slate-500">
                Merge apply is not yet exposed — snapshot only.
              </div>
            </div>

            {selectedIds.length > 0 && (
              <div className="border rounded-lg p-3 bg-blue-50 mb-4" data-testid="dedupe-snapshot-panel">
                <div className="text-sm font-medium text-blue-900 mb-2">
                  Create snapshot batch for {selectedIds.length} selected cluster(s)
                </div>
                <p className="text-xs text-blue-800 mb-2">
                  This captures every employee, QC violation, document and user record for the
                  selected clusters into <code>merge_undo_log</code>. NO merge or write on
                  employees is performed. You can restore any batch later from the Merge Undo Log.
                </p>
                <Textarea
                  data-testid="dedupe-snapshot-reason"
                  value={snapReason}
                  onChange={(e) => setSnapReason(e.target.value)}
                  placeholder="Why are you snapshotting these clusters? (≥10 chars)"
                  className="text-xs mb-2"
                  rows={2}
                />
                <Button
                  onClick={snapshotSelected}
                  disabled={snapshotting || snapReason.trim().length < 10}
                  data-testid="dedupe-snapshot-btn"
                  className="bg-blue-700 hover:bg-blue-800"
                >
                  {snapshotting
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    : <Save className="h-4 w-4 mr-1.5" />}
                  Create snapshot batch
                </Button>
              </div>
            )}

            {(data.clusters || []).length === 0 ? (
              <div className="p-6 text-center text-emerald-600 text-sm" data-testid="dedupe-empty">
                <CheckCircle2 className="h-5 w-5 mx-auto mb-2" />
                No duplicates detected.
              </div>
            ) : (
              <div className="space-y-4 max-h-[520px] overflow-auto">
                {data.clusters.map((c, i) => {
                  const cid = clusterId(c);
                  const checked = safeSet.has(cid);
                  return (
                    <div
                      key={cid}
                      className={`border rounded-lg p-3 ${checked ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50'}`}
                      data-testid={`dedupe-cluster-${i}`}
                    >
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleSafe(cid)}
                            data-testid={`dedupe-safe-checkbox-${i}`}
                          />
                          <span className="text-xs font-medium">Safe to merge</span>
                        </label>
                        <Badge className="bg-slate-700">{c.signal}</Badge>
                        <code className="text-xs text-slate-600 bg-white px-1.5 py-0.5 rounded border">
                          {c.key}
                        </code>
                        <span className="text-xs text-slate-500">size={c.size}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => openPreview(cid)}
                          data-testid={`dedupe-preview-btn-${i}`}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Preview snapshot
                        </Button>
                        <span className="text-xs text-slate-500 italic ml-auto">
                          {c.survivor_reason}
                        </span>
                      </div>
                      <Table className="text-xs">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Action</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>#</TableHead>
                            <TableHead>Nat. ID</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Hired</TableHead>
                            <TableHead>Refs</TableHead>
                            <TableHead>UUID</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <ClusterRow rec={c.survivor} action="KEEP" />
                          {c.losers.map((l) => (
                            <ClusterRow key={l.id} rec={l} action="DROP" />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={!!previewFor} onOpenChange={(o) => !o && setPreviewFor(null)}>
        <DialogContent className="max-w-3xl" data-testid="dedupe-preview-dialog">
          <DialogHeader>
            <DialogTitle>Snapshot preview</DialogTitle>
            <DialogDescription className="text-xs">
              This is exactly what would be captured into <code>merge_undo_log</code> if you
              created a snapshot batch containing only <code>{previewFor?.key}</code>.
            </DialogDescription>
          </DialogHeader>
          {previewFor?.loading && (
            <div className="p-6 text-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          )}
          {previewFor?.error && (
            <div className="text-sm text-rose-600" data-testid="dedupe-preview-error">
              {previewFor.error}
            </div>
          )}
          {previewFor?.payload && (
            <div className="space-y-3 text-xs max-h-[60vh] overflow-auto">
              <div className="grid grid-cols-4 gap-2">
                <Stat label="employees" value={previewFor.payload.counts.employees} />
                <Stat label="qc_violations" value={previewFor.payload.counts.qc_violations} />
                <Stat label="documents" value={previewFor.payload.counts.employee_documents} />
                <Stat label="users" value={previewFor.payload.counts.users} />
              </div>
              <PreviewList title="Employees" items={previewFor.payload.preview.employees} />
              <PreviewList title="QC Violations" items={previewFor.payload.preview.qc_violations} />
              <PreviewList title="Employee Documents" items={previewFor.payload.preview.employee_documents} />
              <PreviewList title="Users" items={previewFor.payload.preview.users} />
              <p className="text-slate-500 italic pt-2 border-t">
                {previewFor.payload.note}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const PreviewList = ({ title, items }) => (
  <div>
    <div className="font-semibold text-slate-800 mb-1">{title} ({items.length})</div>
    {items.length === 0 ? (
      <div className="text-slate-400 pl-2">— none —</div>
    ) : (
      <div className="space-y-1 pl-2">
        {items.map((it, i) => (
          <pre key={i} className="bg-white border rounded p-2 whitespace-pre-wrap break-words">
            {JSON.stringify(it, null, 2)}
          </pre>
        ))}
      </div>
    )}
  </div>
);

const ClusterRow = ({ rec, action }) => (
  <TableRow className={action === 'KEEP' ? 'bg-emerald-50' : ''}>
    <TableCell>
      <Badge className={action === 'KEEP' ? 'bg-emerald-600' : 'bg-rose-600'}>
        {action}
      </Badge>
    </TableCell>
    <TableCell className="max-w-[240px] truncate">
      {rec.full_name_ar || rec.full_name_en || '—'}
    </TableCell>
    <TableCell>{rec.employee_number || '—'}</TableCell>
    <TableCell>{rec.national_id || '—'}</TableCell>
    <TableCell>
      <div className="flex flex-col">
        <span>{rec.status || '—'}</span>
        {rec.is_deleted && <span className="text-[10px] text-rose-600">deleted</span>}
        {rec.is_archived && <span className="text-[10px] text-slate-500">archived</span>}
      </div>
    </TableCell>
    <TableCell>{rec.hire_date || '—'}</TableCell>
    <TableCell>
      <span className={rec.ref_total > 0 ? 'font-semibold' : 'text-slate-400'}>
        {rec.ref_total}
      </span>
    </TableCell>
    <TableCell><code className="text-[10px]">{truncate(rec.id, 12)}</code></TableCell>
  </TableRow>
);

// -----------------------------------------------------------------------------
// Section: Whitelisted Merge (preview + apply)
// -----------------------------------------------------------------------------
const WhitelistedMergeCard = ({ token, onMergeApplied }) => {
  const [whitelist, setWhitelist] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [previews, setPreviews] = useState({});          // key = survivor_id|loser_id
  const [previewLoading, setPreviewLoading] = useState({});
  const [applying, setApplying] = useState({});
  const [confirmInputs, setConfirmInputs] = useState({});
  const [lastResult, setLastResult] = useState({});      // per-pair last apply result
  const [error, setError] = useState(null);

  const fetchWhitelist = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API}/admin/employee-merge/whitelist`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWhitelist(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail?.error || e.message || 'Failed to load whitelist');
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  useEffect(() => { fetchWhitelist(); }, [fetchWhitelist]);

  const pairKey = (e) => `${e.survivor_id}|${e.loser_id}`;

  const runPreview = async (entry) => {
    const k = pairKey(entry);
    setPreviewLoading((p) => ({ ...p, [k]: true }));
    setError(null);
    try {
      const r = await axios.post(
        `${API}/admin/employee-merge/preview`,
        { survivor_id: entry.survivor_id, loser_id: entry.loser_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviews((p) => ({ ...p, [k]: r.data }));
    } catch (e) {
      const msg = e.response?.data?.detail?.error || e.message || 'Preview failed';
      setError(`Preview: ${msg}`);
    } finally {
      setPreviewLoading((p) => ({ ...p, [k]: false }));
    }
  };

  const runApply = async (entry) => {
    const k = pairKey(entry);
    const confirmStr = confirmInputs[k] || '';
    setApplying((p) => ({ ...p, [k]: true }));
    setError(null);
    try {
      const r = await axios.post(
        `${API}/admin/employee-merge/apply`,
        {
          survivor_id: entry.survivor_id,
          loser_id: entry.loser_id,
          confirm: confirmStr,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLastResult((p) => ({ ...p, [k]: r.data }));
      toast.success(`Merge applied — batch ${r.data.batch_id.slice(0, 8)}…`);
      if (onMergeApplied) onMergeApplied();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      const msg = d.error || e.message || 'Apply failed';
      const expected = d.expected ? ` (expected ${d.expected})` : '';
      setError(`Apply: ${msg}${expected}`);
      toast.error(`Merge failed: ${msg}`);
    } finally {
      setApplying((p) => ({ ...p, [k]: false }));
    }
  };

  return (
    <Card data-testid="whitelisted-merge-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          Whitelisted Merge — Preview &amp; Apply
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchWhitelist}
          disabled={loadingList}
          data-testid="wl-merge-refresh"
        >
          {loadingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-4">
          Every write is preceded by a <code>merge_undo_log</code> snapshot of the
          survivor + loser + every affected user row. The confirm string is
          deterministic per cluster and shown below. If anything looks wrong,
          restore the batch from the Merge Undo Log card.
        </p>

        {error && (
          <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-sm" data-testid="wl-merge-error">
            {error}
          </div>
        )}

        {whitelist.length === 0 && !loadingList && (
          <div className="p-6 text-center text-slate-400 text-sm" data-testid="wl-merge-empty">
            The merge whitelist is empty. Adding a pair requires a code change to
            <code className="ml-1">services/employee_merge_apply_service.py</code>.
          </div>
        )}

        <div className="space-y-4">
          {whitelist.map((entry) => {
            const k = pairKey(entry);
            const preview = previews[k];
            const result = lastResult[k];
            const confirmStr = confirmInputs[k] || '';
            const confirmOk = confirmStr === entry.confirm_string_required;
            return (
              <div
                key={k}
                className="border rounded-lg p-4 bg-slate-50"
                data-testid={`wl-merge-entry-${entry.cluster_key}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-slate-700 truncate">
                      cluster_key: <span className="font-semibold">{entry.cluster_key}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Survivor: <code>{truncate(entry.survivor_id, 20)}</code>
                    </div>
                    <div className="text-xs text-slate-500">
                      Loser: <code>{truncate(entry.loser_id, 20)}</code>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 max-w-2xl">
                      Reason: {entry.reason}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runPreview(entry)}
                    disabled={previewLoading[k]}
                    data-testid={`wl-merge-preview-btn-${entry.cluster_key}`}
                  >
                    {previewLoading[k]
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <><Eye className="h-4 w-4 mr-1" />Preview merge</>}
                  </Button>
                </div>

                {preview && (
                  <div
                    className="mt-4 grid md:grid-cols-2 gap-3"
                    data-testid={`wl-merge-preview-panel-${entry.cluster_key}`}
                  >
                    <div className="bg-white border-emerald-200 border rounded p-3">
                      <div className="flex items-center gap-1 text-emerald-700 font-semibold text-sm mb-2">
                        <CheckCircle2 className="h-4 w-4" /> Survivor (KEEP)
                      </div>
                      <MergeSummaryList summary={preview.survivor} />
                    </div>
                    <div className="bg-white border-red-200 border rounded p-3">
                      <div className="flex items-center gap-1 text-red-700 font-semibold text-sm mb-2">
                        <AlertTriangle className="h-4 w-4" /> Loser (REMOVE)
                      </div>
                      <MergeSummaryList summary={preview.loser} />
                    </div>

                    <div className="md:col-span-2 bg-white border rounded p-3 text-sm">
                      <div className="font-semibold text-slate-700 mb-2">
                        Users reassigned ({preview.changes_to_apply.users_reassignments.length})
                      </div>
                      {preview.changes_to_apply.users_reassignments.length === 0 ? (
                        <div className="text-slate-500 text-xs">
                          No user rows currently point at the loser — nothing to move.
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {preview.changes_to_apply.users_reassignments.map((u) => (
                            <div
                              key={u.user_id}
                              className="text-xs font-mono bg-blue-50 border-blue-200 border rounded p-2"
                              data-testid={`wl-merge-user-move-${u.user_id}`}
                            >
                              <div className="font-semibold text-slate-700">{u.email || '(no email)'}</div>
                              <div>employee_id: <span className="text-red-700 line-through">{truncate(u.employee_id_before, 16)}</span> → <span className="text-emerald-700">{truncate(u.employee_id_after, 16)}</span></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2 bg-white border rounded p-3 text-xs">
                      <div className="font-semibold text-slate-700 mb-1">Snapshot (taken BEFORE any write)</div>
                      <div>Employees captured: <code>{preview.snapshot.employees_snapshotted}</code></div>
                      <div>Users captured: <code>{preview.snapshot.users_snapshotted}</code></div>
                      <div className="mt-1 text-slate-500">
                        Undo: {preview.snapshot.restore_endpoint}
                      </div>
                    </div>

                    <div className="md:col-span-2 bg-white border rounded p-3 text-xs">
                      <div className="font-semibold text-slate-700 mb-1">Not changed by this merge</div>
                      <div>QC violations on loser: <code>{preview.not_changed.qc_violations_on_loser}</code></div>
                      <div>Documents on loser: <code>{preview.not_changed.employee_documents_on_loser}</code></div>
                      <div className="text-slate-500 mt-1">{preview.not_changed.note}</div>
                    </div>

                    {/* Apply block */}
                    <div className="md:col-span-2 bg-emerald-50 border border-emerald-200 rounded p-3">
                      <div className="text-sm font-semibold mb-2 text-emerald-800">
                        Confirm and apply
                      </div>
                      <div className="text-xs mb-2 text-slate-700">
                        Type the confirm string exactly to enable the button:
                        <code className="ml-1 bg-white px-1 py-0.5 rounded border">
                          {entry.confirm_string_required}
                        </code>
                      </div>
                      <div className="flex flex-col md:flex-row gap-2">
                        <input
                          type="text"
                          value={confirmStr}
                          onChange={(e) =>
                            setConfirmInputs((p) => ({ ...p, [k]: e.target.value }))
                          }
                          placeholder={entry.confirm_string_required}
                          className="flex-1 px-2 py-1 border rounded font-mono text-sm"
                          data-testid={`wl-merge-confirm-input-${entry.cluster_key}`}
                        />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={!confirmOk || applying[k]}
                              data-testid={`wl-merge-apply-btn-${entry.cluster_key}`}
                            >
                              {applying[k]
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <><PlayCircle className="h-4 w-4 mr-1" />Execute merge</>}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Apply this merge?</AlertDialogTitle>
                              <AlertDialogDescription>
                                A pre-write snapshot batch will be created FIRST.
                                Then the {preview.changes_to_apply.users_reassignments.length} user rows
                                will be re-pointed at the survivor and the loser
                                will be soft-deleted with a merged_into marker.
                                This is reversible via the Merge Undo Log card.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => runApply(entry)}
                                data-testid={`wl-merge-apply-confirm-${entry.cluster_key}`}
                              >
                                Yes — apply
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                )}

                {result && (
                  <div
                    className="mt-3 p-3 bg-white border border-emerald-300 rounded text-xs"
                    data-testid={`wl-merge-result-${entry.cluster_key}`}
                  >
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-1">
                      <CheckCircle2 className="h-4 w-4" /> Merge applied
                    </div>
                    <div>Batch id: <code>{result.batch_id}</code></div>
                    <div>Users reassigned: <code>{result.users_reassigned}</code></div>
                    <div>Loser soft-deleted: <code>{String(result.loser_soft_deleted)}</code></div>
                    <div>Employees snapshotted: <code>{result.employees_snapshotted}</code></div>
                    <div>Users snapshotted: <code>{result.users_snapshotted}</code></div>
                    <div className="mt-1 text-slate-500">
                      Undo: {result.restore_hint}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const MergeSummaryList = ({ summary }) => {
  if (!summary) return null;
  const s = summary.salary || {};
  return (
    <div className="text-xs space-y-0.5 font-mono">
      <div>id: <code>{truncate(summary.id, 16)}</code></div>
      <div>name: {summary.full_name_ar || summary.full_name_en || '—'}</div>
      <div>employee #: <code>{summary.employee_number || '—'}</code></div>
      <div>national_id: <code>{summary.national_id || '—'}</code></div>
      <div>status: {summary.status || '—'} {summary.is_deleted && <Badge variant="destructive" className="ml-1">deleted</Badge>}</div>
      <div>hire_date: {summary.hire_date || '—'}</div>
      <div>job: {summary.job_title || '—'}</div>
      <div>branch_id: <code>{summary.branch_id ? truncate(summary.branch_id, 12) : '—'}</code></div>
      <div>salary: {s.has_salary ? `${s.basic} ${s.currency}` : 'none'}</div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Section: Merge Undo Log
// -----------------------------------------------------------------------------
const MergeUndoCard = ({ token, refreshKey }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/merge-undo/batches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBatches(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBatches(); }, [fetchBatches, refreshKey]);

  const restore = async (id) => {
    setRestoring(id);
    try {
      const r = await axios.post(
        `${API}/admin/merge-undo/batches/${id}/restore?confirm=RESTORE-BATCH`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Batch restored. ${Object.entries(r.data.restored_counts || {})
          .map(([k, v]) => `${k}:${v}`).join(' ')}`,
      );
      fetchBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Card data-testid="merge-undo-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-5 w-5 text-indigo-600" />
          Merge Undo Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3">
          <Button variant="outline" onClick={fetchBatches} disabled={loading} data-testid="merge-undo-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <div className="text-xs text-slate-500">
            Restoring a batch runs <code>replace_one</code> for every snapshot row.
          </div>
        </div>
        {loading ? (
          <div className="p-4 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : batches.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="merge-undo-empty">
            No snapshot batches yet.
          </div>
        ) : (
          <div className="max-h-[300px] overflow-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Clusters</TableHead>
                  <TableHead>Snapshots</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((b) => (
                  <TableRow key={b.batch_id} data-testid={`merge-undo-row-${b.batch_id}`}>
                    <TableCell><code className="text-[10px]">{truncate(b.batch_id, 12)}</code></TableCell>
                    <TableCell className="max-w-[220px] truncate" title={b.reason}>{b.reason}</TableCell>
                    <TableCell>{b.cluster_count}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold">{b.total_snapshots}</span>
                        <span className="text-[10px] text-slate-500">
                          {Object.entries(b.snapshot_counts || {})
                            .map(([k, v]) => `${k}:${v}`).join(' ')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{b.snapshot_taken_at?.slice(0, 19).replace('T', ' ') || '—'}</TableCell>
                    <TableCell>{b.actor_email || '—'}</TableCell>
                    <TableCell>
                      {b.restored ? (
                        <Badge className="bg-slate-500">restored</Badge>
                      ) : (
                        <Badge className="bg-indigo-600">active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={b.restored || restoring === b.batch_id}
                            data-testid={`merge-undo-restore-btn-${b.batch_id}`}
                          >
                            {restoring === b.batch_id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Undo2 className="h-3 w-3 mr-1" />}
                            Restore
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Restore this snapshot batch?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will replay every snapshotted document back to its captured
                              state via full-document <code>replace_one</code>. Idempotent —
                              a batch can only be restored once.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => restore(b.batch_id)}
                              data-testid={`merge-undo-restore-confirm-${b.batch_id}`}
                            >
                              Yes, restore
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Section: Restore Deleted Employee (the Undo button)
// -----------------------------------------------------------------------------
const RestoreDeletedCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/deleted-employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  const restore = async (id) => {
    setRestoring(id);
    try {
      await axios.post(
        `${API}/admin/employees/${id}/restore?confirm=RESTORE`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Employee restored.');
      fetchDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Card data-testid="restore-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Undo2 className="h-5 w-5 text-purple-600" />
          Restore Deleted Employee
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="p-4 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-sm text-emerald-600 flex items-center gap-2" data-testid="restore-empty">
            <ShieldCheck className="h-4 w-4" />
            No soft-deleted employees.
          </div>
        ) : (
          <div className="max-h-[300px] overflow-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>#</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Deleted at</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((e) => (
                  <TableRow key={e.id} data-testid={`deleted-row-${e.id}`}>
                    <TableCell>
                      {(e.personal?.first_name_ar || e.personal?.first_name) || '—'}{' '}
                      {(e.personal?.last_name_ar  || e.personal?.last_name)  || ''}
                    </TableCell>
                    <TableCell>{e.employment?.employee_number || '—'}</TableCell>
                    <TableCell>{e.deletion_reason || '—'}</TableCell>
                    <TableCell>{e.deleted_at || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => restore(e.id)}
                        disabled={restoring === e.id}
                        data-testid={`restore-btn-${e.id}`}
                      >
                        {restoring === e.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Undo2 className="h-3 w-3 mr-1" />}
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Reusable stat card
// -----------------------------------------------------------------------------
const Stat = ({ label, value, highlight, warn, good }) => (
  <div className={
    "px-3 py-2 rounded-md border text-sm " +
    (warn ? "bg-amber-50 border-amber-200 text-amber-900"
      : good ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : highlight ? "bg-blue-50 border-blue-200 text-blue-900"
      : "bg-slate-50 border-slate-200 text-slate-700")
  }>
    <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-lg font-semibold">{String(value)}</div>
  </div>
);

// -----------------------------------------------------------------------------
// Section: Missing Identity Report (read-only + CSV download)
// -----------------------------------------------------------------------------
const MissingIdentityCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/missing-identity-diagnostic`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/missing-identity-diagnostic/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'missing_identity_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.stats;
  const preview = (data?.rows || []).slice(0, 5);

  return (
    <Card data-testid="missing-id-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <IdCard className="h-5 w-5 text-orange-600" />
          Missing ID Report (National ID / Iqama)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="p-6 text-center text-slate-400" data-testid="missing-id-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : !data ? (
          <div className="text-sm text-slate-500">No data.</div>
        ) : !s ? (
          <div
            className="text-sm p-3 rounded bg-amber-50 border border-amber-200 text-amber-800"
            data-testid="missing-id-no-stats"
          >
            The backend returned no stats block. Click Refresh to try again.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Stat label="Employees scanned" value={s.total_scanned} />
              <Stat label="Total missing" value={s.total_missing} highlight={s.total_missing > 0} />
              <Stat label="Missing NID" value={s.missing_national_id} warn={s.missing_national_id > 0} />
              <Stat label="Missing Iqama" value={s.missing_iqama} warn={s.missing_iqama > 0} />
              <Stat label="Missing both" value={s.missing_both} warn={s.missing_both > 0} />
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-4">
              <Button variant="outline" onClick={fetchReport} disabled={loading} data-testid="missing-id-refresh">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
              </Button>
              <Button
                onClick={downloadCsv}
                disabled={downloading || (s?.total_missing ?? 0) === 0}
                data-testid="missing-id-download-csv"
                className="bg-orange-600 hover:bg-orange-700"
              >
                {downloading
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : <FileDown className="h-4 w-4 mr-1.5" />}
                Download CSV
              </Button>
              <div className="text-xs text-slate-500">
                UTF-8 with BOM — opens cleanly in Excel with Arabic names.
              </div>
            </div>

            {preview.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Preview (first 5 of {s.total_missing})
                </div>
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Missing</TableHead>
                      <TableHead>Employee #</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Nationality</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Job</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((r) => (
                      <TableRow key={r.id} data-testid={`missing-id-row-${r.id}`}>
                        <TableCell>
                          <Badge className={
                            r.missing_type === 'both' ? 'bg-rose-600'
                              : r.missing_type === 'national_id' ? 'bg-orange-600'
                              : 'bg-amber-600'
                          }>
                            {r.missing_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{r.employee_number || '—'}</TableCell>
                        <TableCell className="max-w-[240px] truncate">
                          {r.full_name_ar || r.full_name_en || r.full_name || '—'}
                        </TableCell>
                        <TableCell>{r.nationality || '—'}</TableCell>
                        <TableCell>{r.status || '—'}</TableCell>
                        <TableCell className="max-w-[180px] truncate">{r.job_title || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="text-xs text-slate-500 italic mt-2">
                  {data.note}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Section: Identity Bulk Fill (upload filled-in CSV → dry-run → execute)
// -----------------------------------------------------------------------------
const OUTCOME_TONE = {
  set:                     'bg-emerald-600',
  no_change:               'bg-slate-500',
  conflict:                'bg-rose-600',
  conflict_other_employee: 'bg-rose-700',
  invalid_format:          'bg-amber-600',
  wrong_nationality:       'bg-amber-500',
  skip_blank:              'bg-slate-400',
  not_found:               'bg-slate-500',
  failed:                  'bg-rose-500',
};


// -----------------------------------------------------------------------------
// Section: Branch Backfill Diagnostic  (READ-ONLY, no writes)
// -----------------------------------------------------------------------------
// Surfaces the two lists the user needs to produce a branch mapping:
//   List A — distinct employment.location values with counts + suggested
//            fuzzy-matched branch (if any) + match_type.
//   List B — every branch record (id, name_en, name_ar, current_linked_count).
// Also shows employment.department distinct values — some records carry the
// only branch hint in that legacy field.
//
// Nothing writes. Copy JSON + Download CSV give the user an offline artefact
// to review with their advisor before sending back a confirmed mapping.
// -----------------------------------------------------------------------------

const _matchBadge = (t) => {
  const map = {
    exact_en:   'bg-emerald-100 text-emerald-800 border-emerald-200',
    exact_ar:   'bg-emerald-100 text-emerald-800 border-emerald-200',
    normalized: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    contains:   'bg-sky-100    text-sky-800    border-sky-200',
    ambiguous:  'bg-amber-100  text-amber-800  border-amber-200',
    unmatched:  'bg-rose-100   text-rose-800   border-rose-200',
  };
  return map[t] || 'bg-slate-100 text-slate-700 border-slate-200';
};

const _downloadBlob = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Very small CSV escaper — handles Arabic + commas + quotes + newlines.
const _csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};
const _csv = (rows) => rows.map(r => r.map(_csvCell).join(',')).join('\r\n');

const BranchBackfillDiagnosticCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNow = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API}/admin/branch-link-diagnostic`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === 'object' ? d?.error : d;
      setError(msg || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchNow(); }, [fetchNow]);

  const copyJSON = () => {
    if (!data) return;
    const text = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(text).then(
      () => toast.success('Full JSON copied to clipboard.'),
      () => toast.error('Clipboard blocked — try Download CSV instead.'),
    );
  };

  const downloadCSVs = () => {
    if (!data) return;
    // Combined workbook not supported by CSV — download three files
    // named by section so the operator can zip them.
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

    // List A — employment.location values
    const locRows = [[
      'location', 'normalised', 'employee_count',
      'already_linked_count', 'match_type',
      'matched_branch_id', 'matched_branch_name_en', 'matched_branch_name_ar',
    ]];
    for (const r of (data.location_values || [])) {
      const mb = r.matched_branch || {};
      locRows.push([
        r.location, r.normalised, r.employee_count,
        r.already_linked_count, r.match_type,
        mb.id || '', mb.name_en || '', mb.name_ar || '',
      ]);
    }
    _downloadBlob(
      `branch-diagnostic_A_locations_${stamp}.csv`,
      'text/csv;charset=utf-8',
      '\uFEFF' + _csv(locRows),   // BOM for Excel/Arabic
    );

    // List B — branches
    const brRows = [[
      'id', 'name_en', 'name_ar', 'is_active', 'current_linked_count',
    ]];
    for (const b of (data.branches || [])) {
      brRows.push([
        b.id, b.name_en, b.name_ar,
        b.is_active === false ? 'false' : 'true',
        b.current_linked_count,
      ]);
    }
    _downloadBlob(
      `branch-diagnostic_B_branches_${stamp}.csv`,
      'text/csv;charset=utf-8',
      '\uFEFF' + _csv(brRows),
    );

    // List C — employment.department values
    const deptRows = [[
      'department', 'employee_count', 'already_linked_count',
      'employees_without_location_text',
    ]];
    for (const d of (data.department_values || [])) {
      deptRows.push([
        d.department, d.employee_count,
        d.already_linked_count, d.employees_without_location_text,
      ]);
    }
    _downloadBlob(
      `branch-diagnostic_C_departments_${stamp}.csv`,
      'text/csv;charset=utf-8',
      '\uFEFF' + _csv(deptRows),
    );
    toast.success('Downloaded 3 CSVs (locations, branches, departments).');
  };

  const s = data?.summary;
  const locations   = data?.location_values     || [];
  const branches    = data?.branches            || [];
  const departments = data?.department_values   || [];

  return (
    <Card data-testid="branch-diagnostic-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Map className="h-5 w-5 text-indigo-600" />
          Branch Backfill Diagnostic
          <Badge variant="secondary" className="ml-1 text-[10px]">READ-ONLY</Badge>
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={copyJSON}
            disabled={!data || loading}
            data-testid="branch-diag-copy-json"
          >
            Copy JSON
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={downloadCSVs}
            disabled={!data || loading}
            data-testid="branch-diag-download-csv"
          >
            <FileDown className="h-4 w-4 mr-1" />Download CSVs
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={fetchNow}
            disabled={loading}
            data-testid="branch-diag-refresh"
          >
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-600 mb-3 max-w-3xl">
          Read-only sighting of the branch text on employee records.
          Field examined: <code>employment.location</code> (primary), and
          <code>employment.department</code> (legacy Arabic branch labels).
          No employee record is touched. Once you send the confirmed
          location → branch mapping, a separate dry-run tool will backfill
          <code>employment.branch_id</code> under snapshot cover.
        </p>

        {error && (
          <div className="mb-3 p-2 rounded bg-red-50 border-red-200 border text-red-800 text-sm" data-testid="branch-diag-error">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="p-6 text-center text-slate-400" data-testid="branch-diag-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : !data ? (
          <div className="text-sm text-slate-500">No data.</div>
        ) : !s ? (
          <div className="text-sm p-3 rounded bg-amber-50 border border-amber-200 text-amber-800" data-testid="branch-diag-no-summary">
            The backend returned no summary block. Click Refresh.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="branch-diag-summary">
              <Stat label="Employees"       value={s.total_employees ?? 0} />
              <Stat label="Linked"          value={s.linked_to_branch ?? 0} good={(s.linked_to_branch ?? 0) > 0} />
              <Stat label="Has location"    value={s.has_location_text ?? 0} />
              <Stat label="Distinct loc"    value={s.distinct_location_strings ?? 0} />
              <Stat label="Distinct dept"   value={s.distinct_department_strings ?? 0} />
            </div>

            {/* ============ LIST B — BRANCHES ============ */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-slate-700 mb-2">
                List B — Branch records ({branches.length})
              </div>
              <div className="overflow-auto border rounded-md max-h-64" data-testid="branch-diag-branches">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left">
                      <th className="px-2 py-1">id</th>
                      <th className="px-2 py-1">name_en</th>
                      <th className="px-2 py-1">name_ar</th>
                      <th className="px-2 py-1 text-right">linked</th>
                      <th className="px-2 py-1">active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.map(b => (
                      <tr key={b.id} className="border-t" data-testid={`branch-diag-branch-${b.id}`}>
                        <td className="px-2 py-1 font-mono text-[10px]">{truncate(b.id, 20)}</td>
                        <td className="px-2 py-1">{b.name_en || '—'}</td>
                        <td className="px-2 py-1" dir="rtl">{b.name_ar || '—'}</td>
                        <td className="px-2 py-1 text-right font-mono">{b.current_linked_count}</td>
                        <td className="px-2 py-1">
                          {b.is_active === false
                            ? <Badge variant="outline" className="text-[10px]">inactive</Badge>
                            : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                        </td>
                      </tr>
                    ))}
                    {!branches.length && (
                      <tr><td colSpan="5" className="text-center py-4 text-slate-400">No branch records.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ============ LIST A — LOCATION VALUES ============ */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-slate-700 mb-2">
                List A — Distinct employment.location values ({locations.length})
                <span className="ml-2 text-xs text-slate-400 font-normal">
                  {(s.employees_in_unmatched_locations ?? 0)} employees currently under unmatched location text
                </span>
              </div>
              <div className="overflow-auto border rounded-md max-h-96" data-testid="branch-diag-locations">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left">
                      <th className="px-2 py-1">location</th>
                      <th className="px-2 py-1 text-right">employees</th>
                      <th className="px-2 py-1">match_type</th>
                      <th className="px-2 py-1">suggested branch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map(row => {
                      const mb = row.matched_branch;
                      return (
                        <tr
                          key={row.location}
                          className="border-t"
                          data-testid={`branch-diag-loc-${row.normalised}`}
                        >
                          <td className="px-2 py-1 font-medium" dir="auto">{row.location}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.employee_count}</td>
                          <td className="px-2 py-1">
                            <Badge className={`text-[10px] border ${_matchBadge(row.match_type)}`}>
                              {row.match_type}
                            </Badge>
                          </td>
                          <td className="px-2 py-1 text-slate-700">
                            {mb
                              ? <><span dir="auto">{mb.name_en || mb.name_ar}</span> <span className="text-slate-400 font-mono text-[10px]">({truncate(mb.id, 12)})</span></>
                              : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {!locations.length && (
                      <tr><td colSpan="4" className="text-center py-4 text-slate-400">No location text on any employee.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ============ LIST C — DEPARTMENT VALUES (legacy) ============ */}
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">
                List C — Distinct employment.department values ({departments.length})
                <span className="ml-2 text-xs text-slate-400 font-normal">
                  Legacy free-text. Consult when <code>employment.location</code> is blank.
                </span>
              </div>
              <div className="overflow-auto border rounded-md max-h-64" data-testid="branch-diag-departments">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left">
                      <th className="px-2 py-1">department</th>
                      <th className="px-2 py-1 text-right">employees</th>
                      <th className="px-2 py-1 text-right">no location text</th>
                      <th className="px-2 py-1 text-right">already linked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map(d => (
                      <tr
                        key={d.department}
                        className="border-t"
                        data-testid={`branch-diag-dept-${(d.department || '').slice(0, 40)}`}
                      >
                        <td className="px-2 py-1" dir="auto">{d.department}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.employee_count}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.employees_without_location_text}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.already_linked_count}</td>
                      </tr>
                    ))}
                    {!departments.length && (
                      <tr><td colSpan="4" className="text-center py-4 text-slate-400">No department text on any employee.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};


const IdentityBulkFillCard = ({ token }) => {
  const fileRef = React.useRef(null);
  const [file, setFile] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);       // dry-run result
  const [error, setError] = useState(null);
  const [executed, setExecuted] = useState(false);
  const [batches, setBatches] = useState([]);

  const fetchBatches = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/import/identity/batches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBatches(r.data.batches || []);
    } catch (e) {
      // Silent — this card can render without history.
      console.warn('identity batches list failed', e.message);
    }
  }, [token]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const reset = () => {
    setFile(null);
    setReport(null);
    setError(null);
    setExecuted(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const runDryRun = async () => {
    if (!file) return;
    setError(null);
    setReport(null);
    setExecuted(false);
    setDryRunning(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/import/identity/dry-run`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const body = r.data || {};
      setReport(body);
      // Defensive read — a future backend change that returns an error dict
      // on HTTP 200 (as the QC bulk apply did) MUST NOT crash this toast.
      const sum = body.summary;
      if (sum && typeof sum === 'object') {
        const conflicts = (Number(sum.conflict) || 0) + (Number(sum.conflict_other_employee) || 0);
        toast.success(
          `Dry-run complete: ${Number(sum.set_count) || 0} would set, ${conflicts} conflict(s).`,
        );
      } else {
        // Best-effort message — the backend returned an unexpected shape but
        // NOT an HTTP error. Surface something honest rather than crashing.
        toast.message(
          body.error
            ? `Dry-run returned an error: ${body.error}${body.hint ? ' — ' + body.hint : ''}`
            : 'Dry-run finished but the response had no summary block.',
        );
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === 'object' ? d?.error : d;
      setError(typeof d === 'object' ? JSON.stringify(d) : (d || e.message));
      toast.error(msg || 'Dry-run failed');
    } finally {
      setDryRunning(false);
    }
  };

  const execute = async () => {
    if (!report || !report.can_execute || !report.approval_token) return;
    setExecuting(true);
    try {
      const r = await axios.post(
        `${API}/import/identity/execute?identity_batch_id=${report.identity_batch_id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Import-Approval-Token': report.approval_token,
          },
        },
      );
      const body = r.data || {};
      setReport(body);
      setExecuted(true);
      const sum = body.summary;
      if (sum && typeof sum === 'object' && body.approved === true) {
        const setCount = Number(sum.set_count) || 0;
        if (setCount > 0) {
          toast.success(`Applied: ${setCount} identity value(s) written.`);
        } else {
          toast.success('Batch executed — no rows required a write (all conflict/no-change).');
        }
      } else if (body.error) {
        toast.error(
          body.hint
            ? `${body.error}: ${body.hint}`
            : `Execute reported: ${body.error}`,
        );
      } else {
        // Response has no summary and no error — mark executed but flag it.
        toast.message('Execute finished but the response had no summary block.');
      }
      fetchBatches();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === 'object' ? d?.error : d;
      toast.error(msg || 'Execute failed');
    } finally {
      setExecuting(false);
    }
  };

  const s = report?.summary;
  const outcomes = report?.outcomes || [];
  const canExecute = report?.can_execute && !!report.approval_token && !executed;

  return (
    <Card data-testid="identity-bulk-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UploadCloud className="h-5 w-5 text-teal-600" />
          Identity Bulk Fill (Iqama / National ID)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-600 mb-3 max-w-3xl">
          Upload the CSV downloaded from the Missing ID Report with the{' '}
          <code>national_id</code> and/or <code>iqama</code> columns filled in.
          The dry-run <strong>never writes</strong>; review the row-by-row
          outcome and only then click Apply. Existing identity values are
          never overwritten, and no NID/Iqama that already belongs to another
          employee will ever be written.
        </p>

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="identity-bulk-file-input"
            className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-300 file:bg-slate-50 hover:file:bg-slate-100"
          />
          <Button
            onClick={runDryRun}
            disabled={!file || dryRunning}
            data-testid="identity-bulk-dry-run-btn"
            className="bg-teal-600 hover:bg-teal-700"
          >
            {dryRunning
              ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              : <PlayCircle className="h-4 w-4 mr-1.5" />}
            Dry-run
          </Button>
          <Button variant="outline" onClick={reset} data-testid="identity-bulk-reset-btn">
            Reset
          </Button>
        </div>

        {error && (
          <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm mb-3" data-testid="identity-bulk-error">
            {error}
          </div>
        )}

        {report && !s && (
          <div
            className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm mb-3"
            data-testid="identity-bulk-no-summary"
          >
            {report.error
              ? `Report returned an error: ${report.error}${report.hint ? ' — ' + report.hint : ''}`
              : 'The report has no summary block. Try running the dry-run again.'}
          </div>
        )}

        {report && s && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="identity-bulk-stats">
              <Stat label="Rows" value={s.total_rows ?? 0} />
              <Stat label="Will set" value={s.set_count ?? 0} good={(s.set_count ?? 0) > 0} />
              <Stat label="No change" value={s.no_change ?? 0} />
              <Stat label="Conflict" value={(s.conflict ?? 0) + (s.conflict_other_employee ?? 0)} warn={((s.conflict ?? 0) + (s.conflict_other_employee ?? 0)) > 0} />
              <Stat label="Bad rows" value={(s.invalid_format ?? 0) + (s.wrong_nationality ?? 0) + (s.not_found ?? 0) + (s.failed ?? 0) + (s.skip_blank ?? 0)} warn={((s.invalid_format ?? 0) + (s.wrong_nationality ?? 0) + (s.not_found ?? 0) + (s.failed ?? 0)) > 0} />
            </div>

            <div className="border rounded-lg p-3 bg-blue-50 mb-4">
              <div className="text-sm font-medium text-blue-900 mb-1">
                {executed
                  ? 'Batch executed.'
                  : (canExecute
                    ? `Ready to apply ${s.set_count ?? 0} identity write(s).`
                    : 'Dry-run only — no SET rows to apply.')}
              </div>
              {canExecute && (
                <p className="text-xs text-blue-800 mb-2">
                  Applying uses the approval token bound to this exact file
                  SHA-256. Batch id: <code>{report.identity_batch_id}</code>.
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={execute}
                  disabled={!canExecute || executing}
                  data-testid="identity-bulk-apply-btn"
                  className="bg-blue-700 hover:bg-blue-800"
                >
                  {executing
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                  Apply writes
                </Button>
                {report?.identity_batch_id && (
                  <a
                    className="text-xs underline text-blue-700 self-center"
                    href={`${API}/import/identity/batches/${report.identity_batch_id}/signoff?fmt=md`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="identity-bulk-signoff-link"
                  >
                    Download sign-off (Markdown)
                  </a>
                )}
              </div>
            </div>

            <div className="max-h-[420px] overflow-auto border rounded">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">#</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>File value</TableHead>
                    <TableHead>Nationality</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outcomes.map((o) => (
                    <TableRow key={`${o.row_number}-${o.field || 'row'}`} data-testid={`identity-bulk-row-${o.row_number}`}>
                      <TableCell>{o.row_number}</TableCell>
                      <TableCell>
                        <Badge className={OUTCOME_TONE[o.result] || 'bg-slate-500'}>
                          {o.result}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {o.employee_name || o.employee_number || '—'}
                      </TableCell>
                      <TableCell>{o.field || '—'}</TableCell>
                      <TableCell><code className="text-[11px]">{o.file_value || '—'}</code></TableCell>
                      <TableCell>{o.nationality || '—'}</TableCell>
                      <TableCell className="max-w-[280px] truncate" title={o.message}>{o.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {batches.length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
              Recent identity batches
            </div>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Sign-off</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.slice(0, 8).map((b) => (
                  <TableRow key={b.id} data-testid={`identity-bulk-batch-${b.id}`}>
                    <TableCell><code>{b.id.slice(0, 10)}…</code></TableCell>
                    <TableCell className="max-w-[220px] truncate" title={b.file_name}>{b.file_name}</TableCell>
                    <TableCell>
                      <Badge className={b.status === 'executed' ? 'bg-emerald-600' : 'bg-slate-500'}>
                        {b.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{b.created_at?.slice(0, 19).replace('T', ' ') || '—'}</TableCell>
                    <TableCell>{b.uploaded_by_email || '—'}</TableCell>
                    <TableCell>
                      <a
                        className="underline text-blue-700"
                        href={`${API}/import/identity/batches/${b.id}/signoff?fmt=md`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        MD
                      </a>{' '}
                      <a
                        className="underline text-blue-700"
                        href={`${API}/import/identity/batches/${b.id}/signoff?fmt=csv`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        CSV
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Page shell
// -----------------------------------------------------------------------------
const AdminMaintenancePage = () => {
  const { token, user } = useAuth();
  const authorised = user?.role === 'super_admin';
  const [undoRefreshKey, setUndoRefreshKey] = useState(0);

  if (!authorised) {
    return (
      <div className="p-6" data-testid="admin-maintenance-forbidden">
        <Card>
          <CardContent className="p-8 text-center text-slate-600">
            <AlertTriangle className="h-6 w-6 mx-auto mb-3 text-slate-400" />
            Super Admin only.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-maintenance-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Database className="h-6 w-6 text-slate-500" />
          Admin Maintenance
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Data-quality tools. Every write requires an explicit confirm string and is
          fully audit-logged. Merge apply is deliberately not exposed — this page lets
          you (1) list employees missing a National ID or Iqama and bulk-fill them,
          (2) review duplicate clusters, mark them as &quot;safe to merge&quot; and
          snapshot them into an undo log, and (3) restore either soft-deleted
          employees or a previous snapshot batch. QC-specific tools live in the
          QC Violations hub.
        </p>
      </div>

      <MissingIdentityCard token={token} />
      <BranchBackfillDiagnosticCard token={token} />
      <IdentityBulkFillCard token={token} />
      <EmployeeDuplicateCard
        token={token}
        onSnapshotCreated={() => setUndoRefreshKey((k) => k + 1)}
      />
      <WhitelistedMergeCard
        token={token}
        onMergeApplied={() => setUndoRefreshKey((k) => k + 1)}
      />
      <MergeUndoCard token={token} refreshKey={undoRefreshKey} />
      <RestoreDeletedCard token={token} />
    </div>
  );
};

export default AdminMaintenancePage;
